import { createClient } from '@supabase/supabase-js';
import { clearBrandAccessCache } from './brandAccess';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    `Missing Supabase env vars. Need VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY. ` +
    `Got url=${supabaseUrl ? 'set' : 'missing'} key=${supabaseAnonKey ? 'set' : 'missing'}`
  );
}

// Exported so callers (e.g. AppContext's stuck-splash recovery) can wipe
// the exact key supabase-js uses — wiping the default `sb-*` prefix is a
// no-op for us because the client below uses a custom storageKey.
export const SUPABASE_AUTH_STORAGE_KEY = (() => {
  try {
    const host = new URL(supabaseUrl).hostname; // e.g. fypfrokvqrhufzeokkst.supabase.co
    const projectRef = host.split('.')[0];      // fypfrokvqrhufzeokkst
    return `maloo-auth-token-${projectRef}`;
  } catch {
    return 'maloo-auth-token';
  }
})();

const storageKey = SUPABASE_AUTH_STORAGE_KEY;

// No-op lock. supabase-js v2 wraps every auth operation (and the access-token
// retrieval that every DB query depends on) in navigator.locks.request().
// In this app that lock has been wedging — once a single getSession() hangs,
// ALL subsequent auth + db calls block waiting on the lock and the UI is
// dead. We don't need cross-tab serialization for our flows, so we replace
// the lock with a passthrough that just runs `fn` immediately. Signature
// from supabase-js: <R>(name: string, acquireTimeout: number, fn: () => Promise<R>)
const noopLock = async <R,>(_name: string, _acquireTimeout: number, fn: () => Promise<R>): Promise<R> => fn();

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // ✅ Auto-refresh tokens before expiry
    autoRefreshToken: true,
    // ✅ Persist session in localStorage
    persistSession: true,
    // ✅ Detect session in URL (for OAuth callbacks)
    detectSessionInUrl: true,
    // ✅ Use PKCE flow for better security
    flowType: 'pkce',
    // ✅ Custom storage key
    storageKey,
    // ✅ Disable navigator.locks — see comment above
    lock: noopLock,
  },
  // ✅ Global fetch configuration
  global: {
    headers: {
      'x-client-info': 'maloo-app',
    },
  },
  // ✅ Database configuration
  db: {
    schema: 'public',
  },
});

// ✅ Cleanup on sign out
supabase.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_OUT') {
    localStorage.removeItem(storageKey); 
    localStorage.removeItem('maloo-auth-token');
    localStorage.removeItem('supabase.auth.token');
    clearBrandAccessCache();
    console.log('✅ Session cleared');
  }
  
  if (event === 'SIGNED_IN') {
    console.log('✅ User signed in');
  }
  
  if (event === 'TOKEN_REFRESHED') {
    console.log('🔄 Token refreshed');
  }
});